// src/components/ui/SliderCaptcha.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, message } from 'antd';
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { getCaptcha, verifyCaptcha } from '@/api/captcha';
import type { GetCaptchaResponse, CaptchaToken } from '@/types/captcha';
import './SliderCaptcha.css';

interface TrackPoint {
  x: number;
  y: number;
  t: number;
}

interface SliderCaptchaProps {
  visible: boolean;
  scene: 'login' | 'register' | 'reset_password';
  onSuccess: (token: CaptchaToken) => void;
  onClose: () => void;
}

// 【全链路统一尺寸常量】所有环节共用一套基准，彻底杜绝错位变形
const CANVAS_WIDTH = 340;       // 画布宽度（和Modal、轨道完全一致）
const CANVAS_HEIGHT = 210;      // 画布高度
const PUZZLE_SIZE = 50;          // 拼图/凹槽/滑块宽度（正方形，宽高一致）
const PUZZLE_RADIUS = PUZZLE_SIZE / 4; // 圆角半径，和clip-path完全匹配
const MAX_SLIDE_OFFSET = CANVAS_WIDTH - PUZZLE_SIZE; // 滑块最大滑动距离（和targetX范围匹配）

export const SliderCaptcha: React.FC<SliderCaptchaProps> = ({
  visible,
  scene,
  onSuccess,
  onClose,
}) => {
  const [captchaData, setCaptchaData] = useState<GetCaptchaResponse | null>(null);
  const [sliderX, setSliderX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<TrackPoint[]>([]);
  const startTimeRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  // 鼠标在滑块上的偏移量，解决拖动跳变问题
  const mouseOffsetRef = useRef<number>(0);

  // 【凹槽绘制】和拼图clip-path 100%匹配，确保形状完全一致
  const drawPuzzleGap = useCallback((
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    radius: number
  ) => {
    ctx.save();
    ctx.beginPath();
    // 半透明白色填充+蓝色描边，和设计效果完全一致
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.strokeStyle = '#1890ff';
    ctx.lineWidth = 2;
    
    // 与拼图clip-path完全一致的路径，绘制的必然是标准正方形
    ctx.moveTo(x, y + radius);
    ctx.lineTo(x, y + size - radius);
    ctx.quadraticCurveTo(x, y + size, x + radius, y + size);
    ctx.lineTo(x + size - radius, y + size);
    ctx.quadraticCurveTo(x + size, y + size, x + size, y + size - radius);
    ctx.lineTo(x + size, y + radius);
    ctx.quadraticCurveTo(x + size, y, x + size - radius, y);
    ctx.lineTo(x + radius, y);
    ctx.quadraticCurveTo(x, y, x, y + radius);
    
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }, []);

  // 【画布绘制】固定宽高，无拉伸，背景图等比填充
  const drawCaptchaWithGap = useCallback((imageUrl: string, gapX: number, gapY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // 画布属性宽高和CSS宽高完全一致，绝对不会拉伸变形
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    
    img.onload = () => {
      // 清空画布
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      // 背景图按画布尺寸等比绘制，无拉伸
      ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      // 绘制凹槽，尺寸和拼图完全一致
      drawPuzzleGap(ctx, gapX, gapY, PUZZLE_SIZE, PUZZLE_RADIUS);
    };

    img.onerror = () => {
      message.error('验证码背景图加载失败');
    };
  }, [drawPuzzleGap]);

  // 加载验证码
  const loadCaptcha = useCallback(async () => {
    setIsLoading(true);
    setSliderX(0);
    setIsVerified(false);
    trackRef.current = [];
    try {
      const response = await getCaptcha({ scene });
      setCaptchaData(response);
      
      // 等待DOM更新后绘制画布
      setTimeout(() => {
        if (response.target_x && response.target_y) {
          drawCaptchaWithGap(response.background_url, response.target_x, response.target_y);
        }
      }, 50);
    } catch {
      message.error('加载验证码失败');
    } finally {
      setIsLoading(false);
    }
  }, [scene, drawCaptchaWithGap]);

  useEffect(() => {
    if (visible) {
      loadCaptcha();
    }
  }, [visible, loadCaptcha]);

  // 【鼠标按下】记录偏移量，解决拖动跳变
  const handleMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isVerified || isLoading || !sliderTrackRef.current) return;
    
    setIsDragging(true);
    startTimeRef.current = Date.now();
    
    // 获取鼠标/触摸坐标
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    
    // 记录鼠标在滑块上的偏移量（鼠标位置 - 滑块左边缘）
    const trackRect = sliderTrackRef.current.getBoundingClientRect();
    mouseOffsetRef.current = clientX - (trackRect.left + sliderX);
    
    startYRef.current = clientY;
    trackRef.current = [{ x: sliderX, y: 0, t: 0 }];

    // 阻止默认事件，避免页面拖动
    e.preventDefault();
  }, [isVerified, isLoading, sliderX]);

  // 【鼠标拖动】坐标1:1匹配，滑块位置=拼图位置
  const handleMouseMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || isVerified || isLoading || !sliderTrackRef.current) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    
    const trackRect = sliderTrackRef.current.getBoundingClientRect();
    // 减去鼠标偏移量，滑块位置完全跟随鼠标，无跳变
    const rawX = clientX - trackRect.left - mouseOffsetRef.current;
    // 限制滑动范围，和targetX范围完全匹配
    const newX = Math.min(Math.max(rawX, 0), MAX_SLIDE_OFFSET);
    
    setSliderX(newX);
    
    // 记录轨迹
    trackRef.current.push({
      x: newX,
      y: clientY - startYRef.current,
      t: Date.now() - startTimeRef.current,
    });

    e.preventDefault();
  }, [isDragging, isVerified, isLoading]);

  // 【鼠标松开】直接传递sliderX，和视觉位置100%一致
  const handleMouseUp = useCallback(async () => {
    if (!isDragging || isVerified || isLoading || !captchaData) return;
    
    setIsDragging(false);
    
    try {
      const trackData = JSON.stringify(trackRef.current);
      // 直接传递sliderX，拼图刚好覆盖凹槽时，x=targetX，直接验证通过
      await verifyCaptcha({
        session_id: captchaData.session_id,
        track_data: trackData,
        x: sliderX,
      });
      
      setIsVerified(true);
      message.success('验证成功');
      
      onSuccess({
        session_id: captchaData.session_id,
        track_data: trackData,
        x: sliderX,
        verified: true,
      });
      
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch {
      message.error('验证失败，请重试');
      setSliderX(0);
      trackRef.current = [];
    }
  }, [isDragging, isVerified, isLoading, captchaData, sliderX, onSuccess, onClose]);

  const handleRefresh = useCallback(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  if (!visible) return null;

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      width={385}
      centered
      className="slider-captcha-modal"
      maskClosable={false}
      bodyStyle={{ padding: 0 }}
      zIndex={1000}
    >
      <div className="slider-captcha-container">
        <div className="captcha-header">
          <span className="captcha-title">安全验证</span>
          <div className="captcha-actions">
            <ReloadOutlined 
              className="captcha-action-icon" 
              onClick={handleRefresh}
            />
            <CloseOutlined 
              className="captcha-action-icon" 
              onClick={onClose}
            />
          </div>
        </div>
        <div className="captcha-image-wrapper">
          {isLoading ? (
            <div className="captcha-loading">加载中...</div>
          ) : (
            <>
              <canvas 
                ref={canvasRef}
                className="captcha-canvas"
              />
              {/* 拼图left和滑块left完全同步，1:1匹配画布坐标 */}
              <div 
                className="captcha-puzzle-piece"
                style={{ 
                  left: `${sliderX}px`,
                  top: `${captchaData?.target_y || 80}px`,
                }}
              >
                <div className="puzzle-shape" />
              </div>
            </>
          )}
        </div>
        <div 
          ref={sliderTrackRef}
          className={`captcha-slider-track ${isDragging ? 'dragging' : ''} ${isVerified ? 'verified' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        >
          <div className="slider-filled" style={{ width: `${sliderX}px` }} />
          
          <div className="slider-button" style={{ left: `${sliderX}px` }}>
            {isVerified ? (
              <span className="success-icon">✓</span>
            ) : (
              <span className="arrow-icon">→</span>
            )}
          </div>
          
          <span className="slider-text">
            {isVerified ? '验证成功' : '向右滑动完成拼图'}
          </span>
        </div>
        <div className="captcha-footer">
          <span>保护您的账号安全</span>
        </div>
      </div>
    </Modal>
  );
};